import React, { useEffect, useState, useRef, useMemo } from "react";
import { useInjection } from "@presentation/components/dependencyInjection";
import { useJobSetupStore } from "@presentation/stores/JobSetupStore";
import { ListSoftwareVersionsService } from "@application/useCases/softwareVersions/ListSoftwareVersionsService";
import { licensedSoftwareOrNull, shouldQualifyWithEngineName, useLicensedSoftware } from "./useLicensedSoftware";
import { useShallow } from "zustand/react/shallow";

export const VersionSelectSettingsForm: React.FC = () => {
  // Store & Service
  const { softwareVersion, software, setSoftwareVersion } = useJobSetupStore(
    useShallow((state) => ({
      softwareVersion: state.CreateJobState.softwareVersion,
      software: state.CreateJobState.software ?? "Spectronaut",
      setSoftwareVersion: state.setSoftwareVersion,
    })),
  );
  const licensedSoftware = licensedSoftwareOrNull(useLicensedSoftware());
  const injectedListVersionsService = useInjection(ListSoftwareVersionsService);
  const listVersionsService = useRef(injectedListVersionsService).current;
  const softwareVersionReference = useRef(softwareVersion);
  const latestFetchReference = useRef(0);
  const versionLegend = shouldQualifyWithEngineName(licensedSoftware) ? (software === "SpectroMine" ? "SpectroMine Version" : "Spectronaut Version") : "Version";

  useEffect(() => {
    softwareVersionReference.current = softwareVersion;
  }, [softwareVersion]);

  // UI State
  const [versionsList, setVersionsList] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownSize, setDropdownSize] = useState({ width: 450, height: 400 });
  const [dropdownPosition, setDropdownPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const dropdownReference = useRef<HTMLDivElement>(null);
  const triggerReference = useRef<HTMLDivElement>(null);
  const searchInputReference = useRef<HTMLInputElement>(null);

  // 1. Fetch registry versions for the selected search engine.
  useEffect(() => {
    async function fetchVersionList() {
      const fetchId = latestFetchReference.current + 1;
      latestFetchReference.current = fetchId;

      try {
        const selectedSoftware = software === "SpectroMine" ? "SpectroMine" : "Spectronaut";
        setVersionsList([]);

        const listResponse = await listVersionsService.getVersionsListAsync(selectedSoftware);
        // A slower response for a previously selected engine must not overwrite
        // the list for the engine that is selected now.
        if (latestFetchReference.current !== fetchId) {
          return;
        }

        const registryVersions = listResponse ?? [];
        setVersionsList(registryVersions);

        // Resolve the configured default for this engine from the list just fetched.
        const defaultVersion = await listVersionsService.getDefaultSpectronautVersionAsync(registryVersions, selectedSoftware);
        if (latestFetchReference.current !== fetchId) {
          return;
        }
        const currentSoftwareVersion = softwareVersionReference.current;

        if (currentSoftwareVersion) {
          if (!registryVersions.includes(currentSoftwareVersion)) {
            setSoftwareVersion(defaultVersion ?? "");
          }
          return;
        }

        if (defaultVersion) {
          setSoftwareVersion(defaultVersion);
        }
      } catch (error) {
        console.error(`Error fetching software versions`, error);
      }
    }
    fetchVersionList();
  }, [listVersionsService, setSoftwareVersion, software]);

  // 2. Grouping Logic
  const groupedData = useMemo(() => {
    const filtered = versionsList.filter((v) => v.toLowerCase().includes(searchTerm.toLowerCase()));
    const groups: Record<string, string[]> = {};

    for (const v of filtered) {
      const major = v.split(".", 1)[0];
      if (!Object.hasOwn(groups, major)) groups[major] = [];
      groups[major].push(v);
    }

    // Sort versions within each group and sort groups by major version (descending)
    const sortedGroups: Record<string, string[]> = {};
    const sortedMajors = Object.keys(groups).toSorted((a, b) => b.localeCompare(a, undefined, { numeric: true }));
    for (const major of sortedMajors) {
      sortedGroups[major] = groups[major].toSorted((a, b) => b.localeCompare(a, undefined, { numeric: true }));
    }

    return sortedGroups;
  }, [versionsList, searchTerm]);

  // 3. Drag & Resize Handlers
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        setDropdownPosition({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
      }

      if (!(isResizing && dropdownReference.current)) return;

      const rect = dropdownReference.current.getBoundingClientRect();
      setDropdownSize({
        width: Math.max(300, e.clientX - rect.left),
        height: Math.max(200, e.clientY - rect.top),
      });
    };
    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };
    if (isDragging || isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, isResizing, dragStart]);

  useEffect(() => {
    if (!(dropdownOpen && triggerReference.current)) {
      return;
    }

    const rect = triggerReference.current.getBoundingClientRect();
    setDropdownPosition({ x: rect.left, y: rect.bottom + 5 });
    setTimeout(() => searchInputReference.current?.focus(), 0);
  }, [dropdownOpen]);

  // Versions belong to a search engine, so with no licensed engine there is
  // nothing to choose from and the block is hidden entirely.
  if (licensedSoftware !== null && licensedSoftware.length === 0) {
    return null;
  }

  return (
    <fieldset className="k-form-fieldset" style={{ marginTop: "10px" }}>
      <legend className="k-form-legend">
        <strong>{versionLegend}</strong>
      </legend>

      {/* Selector Box */}
      <div
        ref={triggerReference}
        onClick={() => setDropdownOpen(!dropdownOpen)}
        style={{
          border: "1px solid #ccc",
          borderRadius: "3px",
          padding: "8px 12px",
          backgroundColor: "white",
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span>{softwareVersion || "Select version..."}</span>
        <span style={{ fontSize: "10px", color: "#666" }}>{dropdownOpen ? "▲" : "▼"}</span>
      </div>

      {dropdownOpen && (
        <>
          <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }} onClick={() => setDropdownOpen(false)} />
          <div
            ref={dropdownReference}
            style={{
              position: "fixed",
              left: dropdownPosition.x,
              top: dropdownPosition.y,
              width: dropdownSize.width,
              height: dropdownSize.height,
              backgroundColor: "white",
              border: "1px solid #ccc",
              borderRadius: "3px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              zIndex: 1000,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Header */}
            <div
              style={{ padding: "8px 12px", backgroundColor: "#f5f5f5", cursor: "move", display: "flex", justifyContent: "space-between" }}
              onMouseDown={(e) => {
                setIsDragging(true);
                const rect = dropdownReference.current?.getBoundingClientRect();
                if (rect) setDragStart({ x: e.clientX - rect.left, y: e.clientY - rect.top });
              }}
            >
              <span style={{ fontWeight: "600", fontSize: "13px" }}>Versions</span>
              <button onClick={() => setDropdownOpen(false)} style={{ border: "none", background: "none", cursor: "pointer" }}>
                ×
              </button>
            </div>

            {/* Search */}
            <div style={{ padding: "10px 10px 0" }}>
              <div style={{ fontSize: "11px", color: "#64748b", marginBottom: "6px" }}>
                New versions found in the registry are available automatically. You can manage which versions are available on the Software Versions page.
              </div>
            </div>
            <div style={{ padding: "0 10px 10px" }}>
              <input
                ref={searchInputReference}
                style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "3px" }}
                placeholder="Search version..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* List with Grouping */}
            <div style={{ flex: 1, overflowY: "auto" }}>
              {versionsList.length === 0 && (
                <div style={{ padding: "12px", color: "#666", fontSize: "13px" }}>
                  {software === "SpectroMine" ? "No SpectroMine image tags are available from the registry." : "No Spectronaut image tags are available from the registry."}
                </div>
              )}
              {Object.entries(groupedData).map(([major, value]) => (
                <div key={major}>
                  <div style={{ padding: "4px 12px", backgroundColor: "#fafafa", fontSize: "11px", fontWeight: "bold", color: "#888", borderBottom: "1px solid #eee" }}>VERSION {major}</div>
                  {value.map((v) => (
                    <div
                      key={v}
                      style={{
                        padding: "8px 24px",
                        cursor: "pointer",
                        backgroundColor: softwareVersion === v ? "#e3f2fd" : "transparent",
                      }}
                      onClick={() => {
                        setSoftwareVersion(v);
                        setDropdownOpen(false);
                      }}
                    >
                      <span style={{ fontSize: "13px" }}>{v}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {/* Resize Handle */}
            <div
              style={{ position: "absolute", bottom: 0, right: 0, width: "15px", height: "15px", cursor: "se-resize", background: "linear-gradient(135deg, transparent 50%, #999 50%)" }}
              onMouseDown={(e) => {
                e.stopPropagation();
                setIsResizing(true);
              }}
            />
          </div>
        </>
      )}
    </fieldset>
  );
};
